import { assertEvent, assign } from 'xstate5';

import { type OrderingMachineAction } from './ordering-machine.actions.types';
import { type OrderingEvent } from './ordering-machine.events';
import { orderingMachineLog } from './ordering-machine.logger';
import { getNewTip } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

const updateRewards: OrderingMachineAction = assign({
  rewards({ event }) {
    return getRewardsFromEvent(event);
  },
  appliedRewardId({ context, event }) {
    if (!context.appliedRewardId) return undefined;

    const rewards = getRewardsFromEvent(event);
    const appliedReward = rewards.find((reward) => reward.id === context.appliedRewardId);

    return appliedReward?.id;
  },
});

const getRewardsFromEvent = (event: OrderingEvent) => {
  if (event.type === 'ASSIGN_REWARDS') {
    return event.data.rewards;
  }

  if (event.type === 'xstate.done.actor.fetchRewards') {
    return event.output.rewards;
  }

  return [];
};

const clearRewards: OrderingMachineAction = assign({
  rewards: [],
  appliedRewardId: undefined,
});

const onFetchRewardsError: OrderingMachineAction = () => {
  orderingMachineLog.error('Failed to fetch rewards');
};

const setRewardIdBeingUpdated: OrderingMachineAction = assign({
  rewardIdBeingUpdated({ event }) {
    if (event.type === 'APPLY_REWARD') return event.params.rewardId;
    if (event.type === 'REMOVE_REWARD') return event.params.rewardId;
    return undefined;
  },
});

const setRewardTermsBeingShown: OrderingMachineAction = assign({
  rewardTermsBeingShown({ event }) {
    assertEvent(event, 'SHOW_LOYALTY_TERMS');
    return event.params.rewardId;
  },
});

const onApplyRewardDone: OrderingMachineAction = assign({
  rewardIdBeingUpdated: undefined,
  appliedRewardId({ event }) {
    assertEvent(event, 'xstate.done.actor.applyReward');
    if (!('cart' in event.output)) return undefined;
    return event.output.cart?.appliedRewardId;
  },
  cart({ context, event }) {
    assertEvent(event, 'xstate.done.actor.applyReward');
    if (!('cart' in event.output)) return context.cart;
    if (!event.output.cart) return context.cart;

    return { ...event.output.cart, wantedTimes: context.cart.wantedTimes };
  },
  selectedTip({ context, event }) {
    assertEvent(event, 'xstate.done.actor.applyReward');
    if (!('cart' in event.output)) return context.selectedTip;
    return getNewTip(
      context.selectedTip,
      context.cart.maxTipAmount,
      context.availableTips,
      event.output.cart?.ledger,
    );
  },
});

const onRemoveRewardDone: OrderingMachineAction = assign({
  rewardIdBeingUpdated: undefined,
  appliedRewardId: undefined,
  cart({ context, event }) {
    assertEvent(event, 'xstate.done.actor.removeReward');
    if (!('cart' in event.output)) return context.cart;
    if (!event.output.cart) return context.cart;

    return { ...event.output.cart, wantedTimes: context.cart.wantedTimes };
  },
  selectedTip({ context, event }) {
    assertEvent(event, 'xstate.done.actor.removeReward');
    if (!('cart' in event.output)) return context.selectedTip;
    return getNewTip(
      context.selectedTip,
      context.cart.maxTipAmount,
      context.availableTips,
      event.output.cart?.ledger,
    );
  },
});

const onRewardError: OrderingMachineAction = assign({
  rewardIdBeingUpdated: undefined,
  rewardError({ event }) {
    if (event.type === 'xstate.done.actor.applyReward' && 'error' in event.output) {
      return event.output.error;
    }
    if (event.type === 'xstate.done.actor.removeReward' && 'error' in event.output) {
      return event.output.error;
    }
    if (event.type === 'xstate.done.actor.applyPromoCode' && 'error' in event.output) {
      return event.output.error;
    }
    return undefined;
  },
  isRewardMissingSku({ event }) {
    if (event.type === 'xstate.done.actor.applyReward' && 'isRewardMissingSku' in event.output) {
      return Boolean(event.output.isRewardMissingSku);
    }
    if (event.type === 'xstate.done.actor.removeReward' && 'isRewardMissingSku' in event.output) {
      return Boolean(event.output.isRewardMissingSku);
    }
    if (event.type === 'xstate.done.actor.applyPromoCode' && 'isRewardMissingSku' in event.output) {
      return Boolean(event.output.isRewardMissingSku);
    }
    return false;
  },
});

const onApplyPromoCodeDone: OrderingMachineAction = assign({
  rewards({ event }) {
    assertEvent(event, 'xstate.done.actor.applyPromoCode');
    return event.output.rewards;
  },
});

const onApplyGiftCardDone: OrderingMachineAction = assign({
  giftCardBalance({ context, event }) {
    assertEvent(event, 'xstate.done.actor.applyGiftCard');
    return event.output?.giftCardBalance ?? context.giftCardBalance;
  },
  availableCredit({ context, event }) {
    assertEvent(event, 'xstate.done.actor.applyGiftCard');
    return event.output?.availableCredit ?? context.availableCredit;
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const OrderingRewardsActions = {
  updateRewards,
  clearRewards,
  setRewardIdBeingUpdated,
  setRewardTermsBeingShown,
  onApplyRewardDone,
  onRemoveRewardDone,
  onRewardError,
  onApplyPromoCodeDone,
  onApplyGiftCardDone,
  onFetchRewardsError,
};
